/**
 * Betting Tab - Bet options and line configuration
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { SkinBetConfig } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface BettingTabProps {
  config: SkinBetConfig;
  onUpdate: (config: SkinBetConfig) => void;
}

export default function BettingTab({ config, onUpdate }: BettingTabProps) {
  const [newBetOption, setNewBetOption] = useState<string>('');

  const updateField = <K extends keyof SkinBetConfig>(field: K, value: SkinBetConfig[K]) => {
    onUpdate({ ...config, [field]: value });
  };

  const addBetOption = () => {
    const value = Number(newBetOption);
    if (value > 0 && !config.betOptions.includes(value)) {
      const newOptions = [...config.betOptions, value].sort((a, b) => a - b);
      updateField('betOptions', newOptions);
      setNewBetOption('');
    }
  };

  const removeBetOption = (value: number) => {
    if (config.betOptions.length > 1) {
      updateField('betOptions', config.betOptions.filter((v) => v !== value));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Betting Configuration</h2>
        <p className="text-sm text-gray-500 mb-6">
          Configure betting options, paylines, and default values.
        </p>
      </div>

      {/* Paylines */}
      <div>
        <h3 className="text-md font-medium mb-4">Paylines</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Lines
            </label>
            <input
              type="number"
              min={1}
              max={config.maxLines}
              value={config.minLines}
              onChange={(e) => updateField('minLines', Math.max(1, Number(e.target.value)))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimum number of paylines that must be active
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Lines
            </label>
            <input
              type="number"
              min={config.minLines}
              max={100}
              value={config.maxLines}
              onChange={(e) => updateField('maxLines', Math.max(config.minLines, Number(e.target.value)))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Maximum number of paylines available
            </p>
          </div>
        </div>
      </div>

      {/* Bet Options */}
      <div>
        <h3 className="text-md font-medium mb-4">Bet Options</h3>
        <p className="text-sm text-gray-500 mb-4">
          Define the available bet amounts per line. Players will choose from these options.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {config.betOptions.map((value) => (
            <div
              key={value}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
            >
              <span className="font-mono">{value}</span>
              {config.betOptions.length > 1 && (
                <button
                  onClick={() => removeBetOption(value)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 max-w-xs">
          <input
            type="number"
            min={1}
            value={newBetOption}
            onChange={(e) => setNewBetOption(e.target.value)}
            placeholder="Add bet amount"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addBetOption();
              }
            }}
          />
          <button
            onClick={addBetOption}
            disabled={!newBetOption || Number(newBetOption) <= 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {/* Default Values */}
      <div>
        <h3 className="text-md font-medium mb-4">Default Values</h3>
        <p className="text-sm text-gray-500 mb-4">
          Set the initial bet amount and lines when a player starts the game.
        </p>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Bet
            </label>
            <select
              value={config.defaultBet}
              onChange={(e) => updateField('defaultBet', Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {config.betOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Lines
            </label>
            <input
              type="number"
              min={config.minLines}
              max={config.maxLines}
              value={config.defaultLines}
              onChange={(e) => {
                const value = Number(e.target.value);
                updateField('defaultLines', Math.min(Math.max(value, config.minLines), config.maxLines));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div>
        <h3 className="text-md font-medium mb-4">Bet Range Summary</h3>
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary-600">
                {Math.min(...config.betOptions) * config.minLines}
              </div>
              <div className="text-sm text-gray-500">Min Total Bet</div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-primary-600">
                {Math.max(...config.betOptions) * config.maxLines}
              </div>
              <div className="text-sm text-gray-500">Max Total Bet</div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-gray-700">
                {config.defaultBet * config.defaultLines}
              </div>
              <div className="text-sm text-gray-500">Default Total Bet</div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-gray-700">
                {config.betOptions.length}
              </div>
              <div className="text-sm text-gray-500">Bet Options</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <h3 className="text-md font-medium mb-4">Quick Presets</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onUpdate({
              minLines: 1,
              maxLines: 1,
              betOptions: [1, 2, 5, 10, 20, 50, 100],
              defaultBet: 1,
              defaultLines: 1,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Classic 3-Reel
          </button>
          <button
            onClick={() => onUpdate({
              minLines: 1,
              maxLines: 20,
              betOptions: [1, 2, 3, 5, 10],
              defaultBet: 1,
              defaultLines: 20,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Standard 5-Reel
          </button>
          <button
            onClick={() => onUpdate({
              minLines: 1,
              maxLines: 50,
              betOptions: [1, 2, 5, 10, 25, 50],
              defaultBet: 1,
              defaultLines: 50,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            50 Paylines
          </button>
          <button
            onClick={() => onUpdate({
              minLines: 243,
              maxLines: 243,
              betOptions: [1, 2, 5, 10, 20],
              defaultBet: 1,
              defaultLines: 243,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            243 Ways
          </button>
        </div>
      </div>
    </div>
  );
}
